import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { MIN_TEMPLATE_GAP_DAYS, MAX_LADDER_TEMPLATES } from '@claimup/shared/constants/ar-arranque';
import EmailSchedule from '../SettingsPage/components/email/EmailSchedule';
import { ponerPeldano, editarPlantillaDeCliente } from '../../services/kapsoTemplatesService';
import ArranqueExistentes from './ArranqueExistentes';
import AIAgentReviewSettings from '../AIAgentPage/components/AIAgentReviewSettings';

/**
 * «Configuración»: aquí se crean los flujos de comunicación de la empresa — qué
 * sale, cuándo y en qué orden, y qué revisa una persona antes de que salga.
 * Los TEXTOS de los mensajes están en «Plantillas» (WhatsApp, correo y SMS).
 *
 * El arranque de los expedientes AR: lo que se manda antes de que el deudor
 * conteste. Correo (se configura en su pantalla), los días de cada plantilla de
 * WhatsApp, la primera llamada, con lo que pasa después de ella, y el SMS.
 *
 * La cambia la empresa; la ven sus gestores y el admin (que elige la empresa).
 * Lo que se deja vacío no sale solo. Ver docs/ar-arranque-plan.md y
 * docs/ar-sms-plan.md.
 */

const ROLES = ['company', 'gestor', 'admin'];
const DIAS_SEMANA = ['', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

const APROBACION = {
  approved: ['Aprobada', 'bg-emerald-50 text-emerald-700'],
  pending: ['En revisión de Meta', 'bg-amber-50 text-amber-700'],
  rejected: ['Rechazada', 'bg-red-50 text-red-700'],
};

const vacioANull = v => (v === '' || v == null ? null : Number(v));

/**
 * Si Meta la ha aprobado o no. **Va en los dos listados, dentro y fuera de la
 * escalera**: una sin aprobar no la puede mandar nadie —ni el gestor a mano ni
 * el agente para un seguimiento—, así que enseñarla sin el estado hace de
 * «existe» una promesa falsa. Fuera de la escalera es donde más importa, porque
 * es donde viven las de seguimiento recién escritas.
 */
const Aprobacion = ({ plantilla: t }) => {
  const [estado, color] = APROBACION[t.approval_status] || [
    'Sin enviar a Meta',
    'bg-slate-100 text-slate-600',
  ];
  return <span className={`whitespace-nowrap rounded-lg px-2 py-0.5 text-xs ${color}`}>{estado}</span>;
};

/** Los sitios que hay. Son los del tope: el cuarto no existe. */
const PELDANOS = Array.from({ length: MAX_LADDER_TEMPLATES }, (_, i) => i + 1);

/**
 * Dónde va esta plantilla en el contacto en frío.
 *
 * Vive aquí y no en «Plantillas» porque **el orden no es de la plantilla, es del
 * circuito**: la misma razón por la que los días están en esta pantalla, y lo
 * que ya hace el correo (`EmailSchedule`). Y ordenar es una decisión sobre el
 * conjunto: desde la ficha de una sola no se ve quién ocupa el sitio de al lado.
 *
 * **Lo pone el cliente y cambia al momento.** Hasta el 21/10/2026 esto dejaba
 * una propuesta esperando el visto bueno de ClaimUp; se quitó porque aquella
 * revisión no decidía nada — su única salida era «sí». Lo que protege el número
 * del cliente ante Meta son los dos topes del servidor, no una firma.
 * Ver docs/escalera-con-topes-plan.md.
 */
const Peldano = ({ plantilla: t, disabled, onElegir }) => (
  <select
    aria-label={`Contacto nº de ${t.description || t.key}`}
    value={t.order ?? ''}
    disabled={disabled}
    onChange={e => onElegir(e.target.value === '' ? null : Number(e.target.value))}
    className='rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none disabled:bg-slate-50'
  >
    <option value=''>— fuera</option>
    {PELDANOS.map(n => (
      <option key={n} value={n}>
        {n}
      </option>
    ))}
  </select>
);

/** Lo que se manda al servidor: los campos vacíos van como `null` (= no sale solo / sin tope). */
export const toPayload = form => ({
  // Solo los peldaños llevan día: el servidor rechaza una clave que no esté en
  // la escalera, y desde que la pantalla enseña también las de fuera, mandarlas
  // todas sería un 400 seguro.
  whatsapp: form.whatsapp
    .filter(t => t.order != null)
    .map(t => ({ key: t.key, delay_days: vacioANull(t.delay_days) })),
  calls: {
    first_call_delay_days: vacioANull(form.calls.first_call_delay_days),
    gestor_id: form.calls.gestor_id || null,
    call_back_hours: Number(form.calls.call_back_hours),
    no_answer_retry_hours: Number(form.calls.no_answer_retry_hours),
    no_answer_max_attempts: vacioANull(form.calls.no_answer_max_attempts),
  },
  ...(form.sms && {
    sms: {
      enabled: Boolean(form.sms.enabled),
      first_delay_days: vacioANull(form.sms.first_delay_days),
      second_delay_days: vacioANull(form.sms.second_delay_days),
      // Los textos no viajan: se escriben en Plantillas y el servidor los conserva.
    },
  }),
});

const fromConfig = c => ({
  ...c,
  whatsapp: c.whatsapp.map(t => ({ ...t, delay_days: t.delay_days ?? '' })),
  calls: {
    ...c.calls,
    first_call_delay_days: c.calls.first_call_delay_days ?? '',
    gestor_id: c.calls.gestor_id || '',
    no_answer_max_attempts: c.calls.no_answer_max_attempts ?? '',
  },
  ...(c.sms && {
    sms: {
      ...c.sms,
      first_delay_days: c.sms.first_delay_days ?? '',
      second_delay_days: c.sms.second_delay_days ?? '',
    },
  }),
});

/**
 * El día acumulado de cada peldaño, como lo piensa la empresa («día 0, día 3»).
 * Se corta en el primero sin día: el arranque se para ahí.
 */
export function diasAcumulados(whatsapp) {
  // Las de fuera de la escalera no cuentan: no salen solas, así que no suman
  // días ni cortan la cuenta de las que sí.
  let total = 0;
  let cortado = false;
  return whatsapp.map(t => {
    if (cortado || t.delay_days === '' || t.delay_days == null) {
      cortado = true;
      return null;
    }
    total += Number(t.delay_days);
    return total;
  });
}

function ArArranquePage() {
  const { user } = userStore();
  const esAdmin = user?.role === 'admin';
  const [empresas, setEmpresas] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (esAdmin && !companyId) return;
    try {
      const { data } = await api.get(
        '/ar-arranque/settings',
        esAdmin ? { params: { company_id: companyId } } : undefined
      );
      setForm(fromConfig(data.data));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [esAdmin, companyId]);

  useEffect(() => {
    if (!ROLES.includes(user?.role)) return;
    cargar();
  }, [user?.role, cargar]);

  useEffect(() => {
    if (!esAdmin) return;
    api
      .get('/ai-agent/ar-companies')
      .then(({ data }) => setEmpresas(data.data || []))
      .catch(err => toast.error(getErrorMessage(err)));
  }, [esAdmin]);

  if (!ROLES.includes(user?.role)) return <Navigate to='/dashboard' replace />;

  const editable = Boolean(form?.can_edit);
  // Por CLAVE y no por índice: la tabla ya solo itera la escalera, así que el
  // índice dejaría de coincidir en cuanto una plantilla entre o salga de ella.
  const setDelay = (key, value) =>
    setForm(f => ({ ...f, whatsapp: f.whatsapp.map(t => (t.key === key ? { ...t, delay_days: value } : t)) }));
  /**
   * Poner el peldaño. Lo mismo para la empresa y para el admin: el servidor
   * acepta o explica por qué no (tope de 3, o ese sitio ya es de otra).
   */
  const elegirPeldano = (key, orden) => async () => {
    try {
      if (esAdmin) await editarPlantillaDeCliente(companyId, key, { escalation_order: orden });
      else await ponerPeldano(key, orden);
      toast.success(orden == null ? 'Fuera de la escalera' : `Pasa a ser el contacto nº ${orden}`);
      await cargar();
    } catch (err) {
      toast.error(err.message || 'No se ha podido cambiar');
    }
  };

  const setCall = (key, value) => setForm(f => ({ ...f, calls: { ...f.calls, [key]: value } }));
  const setSms = (key, value) => setForm(f => ({ ...f, sms: { ...f.sms, [key]: value } }));

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/ar-arranque/settings', toPayload(form));
      setForm(fromConfig(data.data));
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // La escalera es lo que tiene días; las de fuera solo se ordenan. Separarlas
  // aquí evita que el índice de los acumulados se descuadre al meter una nueva.
  const escalera = form ? form.whatsapp.filter(t => t.order != null) : [];
  const fueraDeEscalera = form ? form.whatsapp.filter(t => t.order == null) : [];
  const acumulados = diasAcumulados(escalera);
  const input = 'rounded-lg border-slate-300 text-sm disabled:bg-slate-50';

  return (
    <MainLayout>
      <div className='space-y-4 max-w-4xl'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <h1 className='text-2xl font-semibold text-slate-900'>Configuración</h1>
          <p className='text-sm text-slate-500 mt-1'>
            Aquí se crean los flujos de comunicación: qué se manda a cada deudor nada más importarlo, cuándo y en qué
            orden, hasta que conteste. En cuanto conteste, deja de ser automático y lo lleva la revisión de la IA. Lo
            que dejes vacío no sale solo. Los textos de cada mensaje se escriben en las plantillas.
          </p>
          {esAdmin && (
            <select
              aria-label='Empresa'
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
              className={`mt-3 ${input}`}
            >
              <option value=''>Elige una empresa…</option>
              {empresas.map(e => (
                <option key={e.company_id} value={e.company_id}>
                  {e.company_name}
                </option>
              ))}
            </select>
          )}
          {form && !editable && (
            <p className='text-xs text-slate-500 mt-2'>Solo la empresa puede cambiar el arranque.</p>
          )}
        </header>

        {!form && (!esAdmin || companyId) && <p className='text-sm text-slate-500'>Cargando…</p>}

        {form && (
          <>
            <section className='bg-white border border-slate-200 rounded-lg px-5 py-4' aria-label='Correo'>
              <h2 className='text-sm font-semibold text-slate-800'>Correo</h2>
              {user.role === 'company' || esAdmin ? (
                <div className='mt-2'>
                  <EmailSchedule companyId={esAdmin ? companyId : user.id} />
                  <Link
                    to='/plantillas?canal=correo'
                    className='mt-2 inline-block text-sm text-indigo-600 hover:text-indigo-800'
                  >
                    Los textos del correo, en Plantillas
                  </Link>
                </div>
              ) : (
                <p className='text-sm text-slate-600 mt-1'>
                  {form.email.enabled
                    ? `Encendido: sale los ${form.email.days.map(d => DIAS_SEMANA[d]).join(', ')} a las ${form.email.send_hour}:00.`
                    : 'Apagado: el arranque no manda correo.'}
                </p>
              )}
            </section>

            <section className='bg-white border border-slate-200 rounded-lg px-5 py-4' aria-label='WhatsApp'>
              <h2 className='text-sm font-semibold text-slate-800'>WhatsApp</h2>
              <p className='text-xs text-slate-500 mt-1'>
                Días que espera cada plantilla desde la anterior; la primera, desde que se importa. Entre dos
                plantillas, al menos {MIN_TEMPLATE_GAP_DAYS} día. Una plantilla sin día no sale sola, ni las que van
                detrás.
              </p>
              {escalera.length === 0 ? (
                <p className='text-sm text-slate-600 mt-3'>
                  No hay plantillas en la escalera.{' '}
                  {user.role === 'company' && (
                    <Link to='/plantillas?canal=whatsapp' className='text-indigo-600 hover:text-indigo-800'>
                      Plantillas
                    </Link>
                  )}
                </p>
              ) : (
                <table className='min-w-full text-sm mt-3'>
                  <tbody className='divide-y divide-slate-100'>
                    {escalera.map((t, i) => {
                      // El aviso de «muy seguida» ya no hace falta: con el suelo
                      // en 7 días no hay nada entre lo permitido y lo imprudente.
                      return (
                        <tr key={t.key}>
                          <td className='py-2 pr-3'>
                            <Peldano
                              plantilla={t}
                              disabled={!editable}
                              onElegir={orden => elegirPeldano(t.key, orden)()}
                            />
                          </td>
                          <td className='py-2 pr-3'>
                            <p className='font-medium text-slate-800'>{t.key}</p>
                            <p className='text-xs text-slate-500 line-clamp-1'>{t.body_text}</p>
                          </td>
                          <td className='py-2 pr-3'>
                            <Aprobacion plantilla={t} />
                          </td>
                          <td className='py-2 pr-3 whitespace-nowrap'>
                            <input
                              type='number'
                              min={i === 0 ? 0 : MIN_TEMPLATE_GAP_DAYS}
                              aria-label={`Días de ${t.key}`}
                              value={t.delay_days}
                              disabled={!editable}
                              onChange={e => setDelay(t.key, e.target.value)}
                              className={`w-20 ${input}`}
                            />{' '}
                            días
                          </td>
                          <td className='py-2 text-xs'>
                            {acumulados[i] == null ? (
                              <span className='text-slate-400'>No sale sola</span>
                            ) : (
                              <span className='text-slate-600'>Día {acumulados[i]}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Las que existen y NO salen solas. Están aquí porque ordenar es
                  una decisión sobre el conjunto: sin verlas, no hay forma de
                  meter una en la escalera. */}
              {fueraDeEscalera.length > 0 && (
                <div className='mt-4 border-t border-slate-100 pt-3'>
                  <p className='text-xs font-medium text-slate-600'>Fuera de la escalera</p>
                  <p className='text-xs text-slate-500'>
                    Existen para esta empresa pero no salen solas: se mandan a mano, y el agente puede
                    elegirlas para un seguimiento.
                  </p>
                  <ul className='mt-2 divide-y divide-slate-100'>
                    {fueraDeEscalera.map(t => (
                      <li key={t.key} className='flex flex-wrap items-center justify-between gap-3 py-2'>
                        <div className='min-w-0'>
                          <p className='text-sm text-slate-800'>{t.description || t.key}</p>
                          <p className='text-xs text-slate-500 line-clamp-1'>{t.body_text}</p>
                        </div>
                        <div className='flex items-center gap-2'>
                          <Aprobacion plantilla={t} />
                          <Peldano
                            plantilla={t}
                            disabled={!editable}
                            onElegir={orden => elegirPeldano(t.key, orden)()}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section className='bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-3' aria-label='Llamadas'>
              <h2 className='text-sm font-semibold text-slate-800'>Llamadas</h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700'>
                <label className='flex items-center gap-2'>
                  Primera llamada el día
                  <input
                    type='number'
                    min='0'
                    aria-label='Día de la primera llamada'
                    value={form.calls.first_call_delay_days}
                    disabled={!editable}
                    onChange={e => setCall('first_call_delay_days', e.target.value)}
                    className={`w-20 ${input}`}
                  />
                </label>
                <label className='flex items-center gap-2'>
                  La hace
                  <select
                    aria-label='Gestor que llama'
                    value={form.calls.gestor_id}
                    disabled={!editable}
                    onChange={e => setCall('gestor_id', e.target.value)}
                    className={input}
                  >
                    <option value=''>Nadie (no se llama)</option>
                    {form.gestores.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className='flex items-center gap-2'>
                  Tras «Volver a llamar», a las
                  <input
                    type='number'
                    min='1'
                    step='0.1'
                    aria-label='Horas tras volver a llamar'
                    value={form.calls.call_back_hours}
                    disabled={!editable}
                    onChange={e => setCall('call_back_hours', e.target.value)}
                    className={`w-20 ${input}`}
                  />
                  horas
                </label>
                <label className='flex items-center gap-2'>
                  Si no contesta, otra a las
                  <input
                    type='number'
                    min='1'
                    step='0.1'
                    aria-label='Horas tras no contestar'
                    value={form.calls.no_answer_retry_hours}
                    disabled={!editable}
                    onChange={e => setCall('no_answer_retry_hours', e.target.value)}
                    className={`w-20 ${input}`}
                  />
                  horas
                </label>
                <label className='flex items-center gap-2'>
                  Parar tras
                  <input
                    type='number'
                    min='1'
                    aria-label='Tope de llamadas sin respuesta'
                    placeholder='sin tope'
                    value={form.calls.no_answer_max_attempts}
                    disabled={!editable}
                    onChange={e => setCall('no_answer_max_attempts', e.target.value)}
                    className={`w-24 ${input}`}
                  />
                  llamadas sin respuesta
                </label>
              </div>
              <p className='text-xs text-slate-500'>
                El arranque pone una sola llamada; las siguientes salen de ella. Sin tope, se reintenta mientras nadie
                tipifique otra cosa.
              </p>
            </section>

            {form.sms && (
              <section className='bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-3' aria-label='SMS'>
                <h2 className='text-sm font-semibold text-slate-800'>SMS</h2>
                <p className='text-xs text-slate-500'>
                  Sale desde un número de ClaimUp que el deudor no conoce, y el deudor puede contestar: su respuesta
                  entra en la revisión como un WhatsApp. Como mucho salen {form.sms.max_outbound} SMS por expediente sin
                  que el deudor haya escrito; contestarle no tiene límite. Apagado, no sale ningún SMS.
                </p>
                <label className='flex items-center gap-2 text-sm text-slate-700'>
                  <input
                    type='checkbox'
                    aria-label='Usar el SMS'
                    checked={Boolean(form.sms.enabled)}
                    disabled={!editable}
                    onChange={e => setSms('enabled', e.target.checked)}
                    className='rounded-lg border-slate-300'
                  />
                  Usar el SMS
                </label>
                {form.sms.enabled && (
                  <div className='space-y-3 text-sm text-slate-700'>
                    {[
                      ['first', 'Primer SMS el día', 'Día del primer SMS', 'desde que se importa'],
                      ['second', 'Segundo SMS', 'Días del segundo SMS', 'días después del primero'],
                    ].map(([clave, etiqueta, aria, despues]) => {
                      const texto = form.sms[`template_${clave}`];
                      return (
                        <div key={clave} className='space-y-1'>
                          <label className='flex items-center gap-2'>
                            {etiqueta}
                            <input
                              type='number'
                              min={clave === 'first' ? 0 : 1}
                              aria-label={aria}
                              value={form.sms[`${clave}_delay_days`]}
                              disabled={!editable}
                              onChange={e => setSms(`${clave}_delay_days`, e.target.value)}
                              className={`w-20 ${input}`}
                            />
                            {despues}
                          </label>
                          {/* El texto se escribe en Plantillas; aquí solo se ve cuál saldrá. */}
                          {texto ? (
                            <p className='text-xs text-slate-500 line-clamp-1'>{texto}</p>
                          ) : (
                            <p className='text-xs text-amber-700'>Sin texto: no sale hasta que lo escribas.</p>
                          )}
                        </div>
                      );
                    })}
                    <p className='text-xs text-slate-500'>
                      Sale de lunes a viernes, de 9 a 20. Un día vacío no sale solo.{' '}
                      <Link to='/plantillas?canal=sms' className='text-indigo-600 hover:text-indigo-800'>
                        Los textos del SMS, en Plantillas
                      </Link>
                    </p>
                  </div>
                )}
              </section>
            )}

            {editable && (
              <button
                type='button'
                onClick={guardar}
                disabled={saving}
                className='px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60'
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            )}

            {/* Lo de arriba vale para los expedientes NUEVOS. Esto aplica el
                arranque a los que ya estaban, que no se enteran de un cambio
                de configuración posterior: su tarea ya corrió. */}
            <ArranqueExistentes companyId={companyId} esAdmin={esAdmin} />

            {/* Qué revisa una persona y qué sale solo: es parte del flujo, así que
                vive aquí y no en la cola de revisión, que es del gestor. */}
            {user.role === 'company' && (
              <section aria-label='Revisión de la IA'>
                <h2 className='text-sm font-semibold text-slate-800 mb-2'>Qué revisa una persona y qué sale solo</h2>
                <AIAgentReviewSettings basePath='/ar-review' />
              </section>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default ArArranquePage;
